import { defer, EMPTY, Observable, of } from 'rxjs';
import { catchError, expand, map, scan, takeLast } from 'rxjs/operators';
import { AlertService } from 'src/app/diagnostics/component/alert/alert.service';
import { ContributionDto, ContributionSourceTypes } from '../../models/contribution';
import { isUnsetDate } from '../../issue-formatting';

export interface AlertHit { id: string; atMs: number; subject: string; ruleId?: string; severity?: string; }
export interface AlertHitResult { hits: AlertHit[]; limited: boolean; failed: boolean; }
export interface AlertHitScope { ruleId: string; fromMs: number; toMs: number; }

// Alert creation and Issue capture use separate timestamps.
export const ALERT_HIT_TIME_TOLERANCE_MS = 60_000;

const PAGE_SIZE = 50;
const MAX_PAGES = 4;

export function alertHitScopes(contributions: ContributionDto[]): AlertHitScope[] {
  return (contributions ?? []).filter(c => c.sourceType === ContributionSourceTypes.Alert
    && !!c.ruleId && !isUnsetDate(c.firstFiredAt) && !isUnsetDate(c.lastFiredAt))
    .map(c => ({ ruleId: c.ruleId, fromMs: Date.parse(c.firstFiredAt), toMs: Date.parse(c.lastFiredAt) }))
    .filter(s => Number.isFinite(s.fromMs) && Number.isFinite(s.toMs) && s.toMs >= s.fromMs);
}

// The endpoint returns alert rows, not occurrence records. Bound all pages together.
export function loadAlertHits(service: AlertService, vin: string, scopes: AlertHitScope[]): Observable<AlertHitResult> {
  if (!vin || !scopes.length) return of({ hits: [], limited: false, failed: false });
  return defer(() => {
    const rules = [...new Set(scopes.map(s => s.ruleId))];
    const from = new Date(Math.min(...scopes.map(s => s.fromMs)) - ALERT_HIT_TIME_TOLERANCE_MS).toISOString();
    const to = new Date(Math.max(...scopes.map(s => s.toMs)) + ALERT_HIT_TIME_TOLERANCE_MS).toISOString();
    const seen = new Set<string>();
    let latest: AlertHitResult = { hits: [], limited: false, failed: false };
    const read = (index: number) => service.getIssueAlertHistory(rules, vin, from, to, index * PAGE_SIZE, PAGE_SIZE)
      .pipe(map(page => ({ page, index })));
    return read(0).pipe(
      expand(({ page, index }) => {
        return (index + 1) * PAGE_SIZE < page.count && index + 1 < MAX_PAGES
          ? read(index + 1) : EMPTY;
      }),
      scan((result: AlertHitResult, { page, index }) => {
        const hits = [...result.hits];
        for (const row of (page?.documents ?? []).slice(0, PAGE_SIZE)) {
          const atMs = Date.parse(row.createDateTimeUtc);
          if (!row.id || seen.has(row.id) || !Number.isFinite(atMs)) continue;
          if (!scopes.some(s => s.ruleId === row.alertRule?.id && atMs >= s.fromMs - ALERT_HIT_TIME_TOLERANCE_MS && atMs <= s.toMs + ALERT_HIT_TIME_TOLERANCE_MS)) continue;
          seen.add(row.id);
          hits.push({ id: row.id, atMs, subject: row.alertRule?.subject ?? '', ruleId: row.alertRule?.id ?? '', severity: row.severity?.name ?? '' });
        }
        return latest = { hits: hits.sort((a, b) => a.atMs - b.atMs), failed: false,
          limited: index + 1 === MAX_PAGES && (index + 1) * PAGE_SIZE < page.count };
      }, { hits: [], limited: false, failed: false } as AlertHitResult),
      catchError(() => of({ ...latest, failed: true })),
      takeLast(1),
    );
  });
}
